import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Card from '@mui/joy/Card';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import DashboardTable from '../../components/DashboardTable';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import CircularProgress from '@mui/joy/CircularProgress';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Alert from '@mui/joy/Alert';
import Avatar from '@mui/joy/Avatar';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import Link from '@mui/joy/Link';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { salesApi } from '../../api/sales.api';
import {
  Sale,
  SaleProduct,
  Payment,
  CreatePaymentDto,
  SaleFulfillmentType,
  SaleShippingCarrier,
} from '../../types/sale';
import { branchesApi } from '../../api/branches.api';
import { addressesApi } from '../../api/addresses.api';
import type { Branch } from '../../types/branch';
import type { Address } from '../../types/address';
import { Message as ChatMessage } from '../../types/message';
import MessageBubble from '../../components/chat/MessageBubble';
import { formatRecordReference } from '../../utils/formatRecordReference';
import { getInitials } from '../../utils/getInitials';
import Grid from '@mui/joy/Grid';
import { LAYOUT } from '../../theme/layoutTokens';
import SaleStatusChip from '../../components/SaleStatusChip';

const SaleDetail: React.FC = () => {
  const { saleReference } = useParams<{ saleReference: string }>();
  const [sale, setSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentType: 'cash' as Payment['paymentType'],
    paymentSource: 'manual_trace' as Payment['paymentSource'],
    status: 'pending' as Payment['status'],
    quote: '',
    expirationDate: '',
    paidAt: '',
  });
  const [branchOptions, setBranchOptions] = useState<Branch[]>([]);
  const [clientAddresses, setClientAddresses] = useState<Address[]>([]);
  const [fulfillmentForm, setFulfillmentForm] = useState<{
    fulfillmentType: SaleFulfillmentType;
    shippingCarrier: SaleShippingCarrier;
    branchReference: string;
    shippingAddressReference: string;
    pickupAddressReference: string;
  }>({
    fulfillmentType: 'undetermined',
    shippingCarrier: 'unknown',
    branchReference: '',
    shippingAddressReference: '',
    pickupAddressReference: '',
  });
  const [fulfillmentSaving, setFulfillmentSaving] = useState(false);
  const [fulfillmentError, setFulfillmentError] = useState<string | null>(null);

  useEffect(() => {
    if (saleReference) {
      loadSale(saleReference as string);
    }
  }, [saleReference]);

  const loadSale = async (saleReference: string) => {
    try {
      setLoading(true);
      const data = await salesApi.getOne(saleReference);
      setSale(data);
      setFulfillmentForm({
        fulfillmentType: data.fulfillmentType ?? 'undetermined',
        shippingCarrier: data.shippingCarrier ?? 'unknown',
        branchReference: data.branch?.reference ?? '',
        shippingAddressReference: data.shippingAddress?.reference ?? '',
        pickupAddressReference: data.pickupAddress?.reference ?? '',
      });
    } catch (error) {
      console.error('Error loading sale:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async (): Promise<void> => {
      try {
        const [branches, clientRef] = await Promise.all([
          branchesApi.list(),
          Promise.resolve(sale?.client?.reference),
        ]);
        setBranchOptions(branches.filter((b) => b.isEnabled));
        if (clientRef) {
          const addr = await addressesApi.getByClientReference(clientRef);
          setClientAddresses(addr);
        } else {
          setClientAddresses([]);
        }
      } catch {
        void 0;
      }
    };
    void run();
  }, [sale?.client?.reference]);

  const openPaymentModal = () => {
    setPaymentForm({
      amount: '',
      paymentType: 'cash',
      paymentSource: 'manual_trace',
      status: 'pending',
      quote: '',
      expirationDate: '',
      paidAt: '',
    });
    setPaymentError(null);
    setPaymentModalOpen(true);
  };

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!saleReference) {
      return;
    }
    setPaymentSaving(true);
    setPaymentError(null);
    try {
      const amountParsed = Number(
        String(paymentForm.amount).replace(',', '.').trim(),
      );
      if (
        paymentForm.amount === '' ||
        Number.isNaN(amountParsed) ||
        amountParsed < 0
      ) {
        setPaymentError('Indicá un importe válido (mayor o igual a 0)');
        setPaymentSaving(false);
        return;
      }
      const payload: CreatePaymentDto = {
        amount: amountParsed,
        paymentType: paymentForm.paymentType,
        paymentSource: paymentForm.paymentSource,
        status: paymentForm.status,
      };
      if (paymentForm.quote.trim() !== '') {
        const q = Number(paymentForm.quote);
        if (Number.isNaN(q)) {
          setPaymentError('Cuota inválida');
          setPaymentSaving(false);
          return;
        }
        payload.quote = q;
      }
      if (paymentForm.expirationDate) {
        payload.expirationDate = paymentForm.expirationDate;
      }
      if (paymentForm.paidAt) {
        payload.paidAt = new Date(paymentForm.paidAt).toISOString();
      }
      const updated = await salesApi.createPayment(saleReference, payload);
      setSale(updated);
      setPaymentModalOpen(false);
    } catch (err) {
      setPaymentError(
        err instanceof Error ? err.message : 'No se pudo registrar el pago',
      );
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleStatusChange = async (
    status: 'pending' | 'completed' | 'cancelled' | 'paid_pending',
  ) => {
    if (!sale || !saleReference) return;
    try {
      const updatedSale = await salesApi.update(saleReference, { status });
      setSale(updatedSale);
    } catch (error) {
      console.error('Error updating sale status:', error);
    }
  };

  const handleSaveFulfillment = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!saleReference) {
      return;
    }
    setFulfillmentSaving(true);
    setFulfillmentError(null);
    try {
      const updated = await salesApi.update(saleReference, {
        fulfillmentType: fulfillmentForm.fulfillmentType,
        shippingCarrier: fulfillmentForm.shippingCarrier,
        branchReference:
          fulfillmentForm.branchReference.trim() === ''
            ? null
            : fulfillmentForm.branchReference,
        shippingAddressReference:
          fulfillmentForm.shippingAddressReference.trim() === ''
            ? null
            : fulfillmentForm.shippingAddressReference,
        pickupAddressReference:
          fulfillmentForm.pickupAddressReference.trim() === ''
            ? null
            : fulfillmentForm.pickupAddressReference,
      });
      setSale(updated);
    } catch (err) {
      setFulfillmentError(
        err instanceof Error ? err.message : 'No se pudo guardar la entrega',
      );
    } finally {
      setFulfillmentSaving(false);
    }
  };

  const formatAddrLine = (a: {
    street: string;
    number: string;
    city: string;
    state: string;
  }): string => `${a.street} ${a.number}, ${a.city}, ${a.state}`;

  const paymentTypeLabel = (t: Payment['paymentType']) => {
    if (t === 'cash') return 'Efectivo';
    if (t === 'transfer') return 'Transferencia';
    return 'Tarjeta';
  };

  const paymentSourceLabel = (s: Payment['paymentSource']) => {
    if (s === 'manual_trace') return 'Trazo manual';
    if (s === 'pos') return 'POS';
    if (s === 'online_gateway') return 'Pasarela online';
    return 'Plan de cuotas';
  };

  const paymentOriginLabel = (o: Payment['origin']) => {
    if (o === 'import') return 'Importación';
    if (o === 'whatsapp') return 'WhatsApp';
    return 'Manual';
  };

  const paymentStatusLabel = (s: Payment['status']) => {
    if (s === 'completed') return 'Completado';
    if (s === 'in_process') return 'En proceso';
    return 'Pendiente';
  };

  const paymentStatusColor = (
    s: Payment['status'],
  ): 'success' | 'warning' | 'primary' | 'neutral' => {
    if (s === 'completed') return 'success';
    if (s === 'in_process') return 'primary';
    return 'warning';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!sale) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Venta no encontrada</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: LAYOUT.contentPadding, maxWidth: LAYOUT.detailMaxWidth, mx: 'auto', width: '100%' }}>

      {/* ── Breadcrumbs ── */}
      <Breadcrumbs aria-label="Ruta" size="sm" sx={{ mb: LAYOUT.breadcrumbsGap, '--Breadcrumbs-gap': '8px' }}>
        <Link component={RouterLink} to="/" underline="hover" color="neutral" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <HomeRoundedIcon sx={{ fontSize: 18 }} />
        </Link>
        <Link component={RouterLink} to="/sales" underline="hover" color="neutral">
          Ventas
        </Link>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          #{formatRecordReference(sale.reference)}
        </Typography>
      </Breadcrumbs>

      {/* ── Título + Volver ── */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: LAYOUT.headerMarginBottom }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography
          level="h1"
          sx={{ fontSize: { xs: '1.35rem', sm: '1.5rem', md: '1.75rem' }, fontWeight: 'lg', letterSpacing: '-0.02em' }}
        >
          Venta #{formatRecordReference(sale.reference)}
        </Typography>
        <Button component={RouterLink} to="/sales" variant="outlined" color="neutral">
          ← Volver
        </Button>
      </Stack>

      <Sheet variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 'md', boxShadow: 'sm' }}>

        {/* Cliente + estado */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 3 }}
          flexWrap="wrap"
          gap={2}
        >
          {/* Cliente con avatar */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              size="sm"
              variant="soft"
              color="primary"
              sx={{ '--Avatar-size': '36px', fontSize: '13px', fontWeight: 'lg' }}
            >
              {getInitials(sale.client?.name ?? '?')}
            </Avatar>
            <Box>
              <Typography level="title-md" fontWeight="lg">
                {sale.client?.name || 'Cliente no encontrado'}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                Fecha: {new Date(sale.date).toLocaleDateString('es-AR')}
              </Typography>
            </Box>
          </Stack>

          {/* Estado */}
          <Stack spacing={1} alignItems="flex-end">
            <SaleStatusChip status={sale.status} />
            <Select
              size="sm"
              value={sale.status}
              onChange={(_, value) =>
                value && handleStatusChange(value as 'pending' | 'completed' | 'cancelled' | 'paid_pending')
              }
              sx={{ minWidth: 220 }}
            >
              <Option value="pending">Pendiente</Option>
              <Option value="paid_pending">Pagada (pendiente entrega)</Option>
              <Option value="completed">Completada</Option>
              <Option value="cancelled">Cancelada</Option>
            </Select>
          </Stack>
        </Stack>

        <Typography level="title-sm" sx={{ mb: 1 }}>Productos</Typography>
        <Sheet variant="outlined" sx={{ overflow: 'auto', mb: 3 }}>
          <DashboardTable>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Precio</th>
                <th>Cantidad</th>
                <th>Stock</th>
                <th style={{ textAlign: 'right' }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {sale.saleProducts?.map((item: SaleProduct) => (
                <tr key={`${item.productReference}-${item.quantity}`}>
                  <td>
                    {item.product?.name ||
                      `Producto #${formatRecordReference(item.productReference)}`}
                  </td>
                  <td>${Number(item.price).toFixed(2)}</td>
                  <td>{item.quantity}</td>
                  <td>
                    {item.product?.quantity != null
                      ? item.product.quantity
                      : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    ${(Number(item.price) * item.quantity).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>
                  ${Number(sale.total).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </DashboardTable>
        </Sheet>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
          <Typography level="title-sm">Pagos</Typography>
          <Button size="sm" color="primary" onClick={openPaymentModal}>
            Registrar pago
          </Button>
        </Stack>
        {sale.payments && sale.payments.length > 0 ? (
          <Sheet variant="outlined" sx={{ overflow: 'auto' }}>
            <DashboardTable>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th style={{ textAlign: 'right' }}>Importe</th>
                  <th>Fuente</th>
                  <th>Origen</th>
                  <th>Estado</th>
                  <th>Cuota</th>
                  <th>Vencimiento</th>
                  <th>Pagado</th>
                </tr>
              </thead>
              <tbody>
                {sale.payments.map((p: Payment) => (
                  <tr key={p.reference}>
                    <td>{paymentTypeLabel(p.paymentType)}</td>
                    <td style={{ textAlign: 'right' }}>
                      ${Number(p.amount ?? 0).toFixed(2)}
                    </td>
                    <td>{paymentSourceLabel(p.paymentSource)}</td>
                    <td>{paymentOriginLabel(p.origin ?? 'manual')}</td>
                    <td>
                      <Chip size="sm" variant="soft" color={paymentStatusColor(p.status)}>
                        {paymentStatusLabel(p.status)}
                      </Chip>
                    </td>
                    <td>{p.quote != null ? p.quote : '—'}</td>
                    <td>{p.expirationDate ? new Date(p.expirationDate).toLocaleDateString('es-AR') : '—'}</td>
                    <td>{p.paidAt ? new Date(p.paidAt).toLocaleString('es-AR') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </DashboardTable>
          </Sheet>
        ) : (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            No hay pagos registrados para esta venta.
          </Typography>
        )}
      </Sheet>

      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 'md',
          boxShadow: 'sm',
          bgcolor: 'background.surface',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.level1',
          }}
        >
          <Typography level="title-lg" sx={{ fontWeight: 'lg' }}>
            Entrega
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.25 }}>
            Modalidad, transporte y direcciones de la venta.
          </Typography>
        </Box>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2 } }}>
          {sale.fulfillmentType === 'shipping' ? (
            <Typography level="body-sm" sx={{ mb: 1.5, color: 'neutral.600' }}>
              {sale.shippingAddress
                ? `Envío a domicilio según dirección acordada: ${formatAddrLine(sale.shippingAddress)}`
                : 'Envío a domicilio: dirección pendiente o completá abajo.'}
            </Typography>
          ) : null}
          {sale.fulfillmentType === 'pickup_branch' ? (
            <Typography level="body-sm" sx={{ mb: 1.5, color: 'neutral.600' }}>
              {sale.branch
                ? `Retiro en sucursal: ${sale.branch.displayName}${
                    sale.branch.address
                      ? ` — ${formatAddrLine(sale.branch.address)}`
                      : ''
                  }`
                : 'Retiro en sucursal: indicá la sucursal abajo.'}
            </Typography>
          ) : null}
          {fulfillmentError ? (
            <Alert color="danger" size="sm" sx={{ mb: 1.5 }}>
              {fulfillmentError}
            </Alert>
          ) : null}
          <Box
            component="form"
            onSubmit={(ev) => void handleSaveFulfillment(ev)}
          >
            <Grid container spacing={LAYOUT.gridSpacing}>
              <Grid xs={12} md={6}>
                <Typography level="title-sm" sx={{ mb: 1, color: 'neutral.700' }}>
                  Envío
                </Typography>
                <Stack spacing={1.25}>
                  <FormControl>
                    <FormLabel>Tipo de entrega</FormLabel>
                    <Select
                      size="sm"
                      value={fulfillmentForm.fulfillmentType}
                      onChange={(_, v) =>
                        v &&
                        setFulfillmentForm((f) => ({
                          ...f,
                          fulfillmentType: v as SaleFulfillmentType,
                        }))
                      }
                    >
                      <Option value="undetermined">Sin definir</Option>
                      <Option value="shipping">Envío</Option>
                      <Option value="pickup_branch">Retiro en sucursal</Option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Transporte (envío)</FormLabel>
                    <Select
                      size="sm"
                      value={fulfillmentForm.shippingCarrier}
                      onChange={(_, v) =>
                        v &&
                        setFulfillmentForm((f) => ({
                          ...f,
                          shippingCarrier: v as SaleShippingCarrier,
                        }))
                      }
                    >
                      <Option value="unknown">Sin definir</Option>
                      <Option value="oca">OCA</Option>
                      <Option value="andreani">Andreani</Option>
                      <Option value="via_cargo">Vía Cargo</Option>
                      <Option value="moto">Moto / cadete</Option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Dirección de envío (cliente)</FormLabel>
                    <Select
                      size="sm"
                      value={fulfillmentForm.shippingAddressReference || null}
                      onChange={(_, v) =>
                        setFulfillmentForm((f) => ({
                          ...f,
                          shippingAddressReference: (v as string) ?? '',
                        }))
                      }
                    >
                      <Option value="">—</Option>
                      {clientAddresses.map((a) => (
                        <Option key={a.reference} value={a.reference}>
                          {a.street} {a.number}, {a.city}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>
              <Grid xs={12} md={6}>
                <Typography level="title-sm" sx={{ mb: 1, color: 'neutral.700' }}>
                  Retiro
                </Typography>
                <Stack spacing={1.25}>
                  <FormControl>
                    <FormLabel>Sucursal (retiro)</FormLabel>
                    <Select
                      size="sm"
                      placeholder="Elegir sucursal"
                      value={fulfillmentForm.branchReference || null}
                      onChange={(_, v) =>
                        setFulfillmentForm((f) => ({
                          ...f,
                          branchReference: (v as string) ?? '',
                        }))
                      }
                    >
                      <Option value="">—</Option>
                      {branchOptions.map((b) => (
                        <Option key={b.reference} value={b.reference}>
                          {b.displayName}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Dirección de retiro (referencia)</FormLabel>
                    <Select
                      size="sm"
                      value={fulfillmentForm.pickupAddressReference || null}
                      onChange={(_, v) =>
                        setFulfillmentForm((f) => ({
                          ...f,
                          pickupAddressReference: (v as string) ?? '',
                        }))
                      }
                    >
                      <Option value="">—</Option>
                      {branchOptions
                        .filter((b) => b.address)
                        .map((b) => (
                          <Option key={`p-${b.reference}`} value={b.address!.reference}>
                            Retiro: {b.displayName} — {formatAddrLine(b.address!)}
                          </Option>
                        ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>
              <Grid xs={12}>
                <Box sx={{ pt: 0.5 }}>
                  <Button type="submit" size="sm" color="primary" loading={fulfillmentSaving}>
                    Guardar entrega
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* ── Conversación vinculada ── */}
      {sale.messages && sale.messages.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography level="title-lg" sx={{ mb: 1 }}>Conversación</Typography>
          <Sheet
            variant="outlined"
            sx={{ p: 2, bgcolor: 'background.level1', maxHeight: 'min(480px, 55vh)', overflowY: 'auto', overflowX: 'hidden' }}
          >
            <Stack spacing={0}>
              {sale.messages.map((message) => (
                <MessageBubble
                  key={message.reference}
                  message={message as ChatMessage}
                  isOwn={Boolean(message.fromMe)}
                />
              ))}
            </Stack>
          </Sheet>
        </Box>
      )}

      {/* ── Modal: Registrar pago ── */}
      <Modal
        open={paymentModalOpen}
        onClose={() => {
          if (!paymentSaving) {
            setPaymentModalOpen(false);
          }
        }}
      >
        <ModalDialog
          component="form"
          onSubmit={handleCreatePayment}
          sx={{ maxWidth: 440, width: '100%' }}
          aria-labelledby="payment-modal-title"
        >
          <ModalClose disabled={paymentSaving} />
          <Typography id="payment-modal-title" level="h4" sx={{ mb: 2 }}>
            Registrar pago
          </Typography>
          {paymentError ? (
            <Alert color="danger" sx={{ mb: 2 }}>
              {paymentError}
            </Alert>
          ) : null}
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Importe</FormLabel>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={(ev) =>
                  setPaymentForm((f) => ({ ...f, amount: ev.target.value }))
                }
                slotProps={{ input: { min: 0, step: '0.01' } }}
                placeholder="0.00"
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Tipo</FormLabel>
              <Select
                value={paymentForm.paymentType}
                onChange={(_, value) =>
                  value &&
                  setPaymentForm((f) => ({
                    ...f,
                    paymentType: value as Payment['paymentType'],
                  }))
                }
              >
                <Option value="cash">Efectivo</Option>
                <Option value="transfer">Transferencia</Option>
                <Option value="card">Tarjeta</Option>
              </Select>
            </FormControl>
            <FormControl required>
              <FormLabel>Fuente</FormLabel>
              <Select
                value={paymentForm.paymentSource}
                onChange={(_, value) =>
                  value &&
                  setPaymentForm((f) => ({
                    ...f,
                    paymentSource: value as Payment['paymentSource'],
                  }))
                }
              >
                <Option value="manual_trace">Trazo manual</Option>
                <Option value="pos">POS</Option>
                <Option value="online_gateway">Pasarela online</Option>
                <Option value="installment_plan">Plan de cuotas</Option>
              </Select>
            </FormControl>
            <FormControl required>
              <FormLabel>Estado</FormLabel>
              <Select
                value={paymentForm.status}
                onChange={(_, value) =>
                  value &&
                  setPaymentForm((f) => ({
                    ...f,
                    status: value as Payment['status'],
                  }))
                }
              >
                <Option value="pending">Pendiente</Option>
                <Option value="in_process">En proceso</Option>
                <Option value="completed">Completado</Option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Cuota (opcional)</FormLabel>
              <Input
                type="number"
                value={paymentForm.quote}
                onChange={(ev) =>
                  setPaymentForm((f) => ({ ...f, quote: ev.target.value }))
                }
                slotProps={{ input: { min: 0, step: 1 } }}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Vencimiento (opcional)</FormLabel>
              <Input
                type="date"
                value={paymentForm.expirationDate}
                onChange={(ev) =>
                  setPaymentForm((f) => ({
                    ...f,
                    expirationDate: ev.target.value,
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Fecha y hora de pago (opcional)</FormLabel>
              <Input
                type="datetime-local"
                value={paymentForm.paidAt}
                onChange={(ev) =>
                  setPaymentForm((f) => ({ ...f, paidAt: ev.target.value }))
                }
              />
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                variant="plain"
                color="neutral"
                type="button"
                disabled={paymentSaving}
                onClick={() => setPaymentModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" loading={paymentSaving}>
                Guardar
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

    </Box>
  );
};

export default SaleDetail;
